import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Icon, Select, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  DEFAULT_PAGE_SIZE,
  operatorJobListResponseSchema,
  operatorOperationsResponseSchema,
  type OperatorChoreState,
  type OperatorJob,
  type OperatorJobState,
  type OperatorOperationsResponse,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey, type Translate } from '../i18n';
import { useOperatorSession } from '../session';
import { ActionDialog } from '../chrome/action-dialog';
import { formatDateTime, formatNumber } from '../chrome/format';
import { Pager } from '../chrome/pager';
import { useToast } from '../chrome/toasts';
import { BTN_SM, PILL, SELECT_SKIN, TH, TH_ROW, TONE } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Háttérmunka* — what the clock runs, and what the queue holds.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **TWO SECTIONS, AS THE REDESIGN DRAWS THEM.** *Ütemezett munka* names each chore the way a
 * person would (*Próbaidők lejáratának ellenőrzése*, not `trial-expiry`), says how its last run went,
 * and turns a late one's whole row red with *Lemaradt · 2 ütem kimaradt*. *Háttérfeladatok* lists
 * individual jobs with a state filter. The first version had a third section — the queue rolled up
 * per kind — which the redesign drops.
 *
 * ⚠️⚠️ **ONE SIGNAL FROM THAT ROLLUP IS KEPT: A KIND WITH NO HANDLER.** A job nothing will ever
 * process is the one fault the queue cannot report by failing, because it never runs. It survives as
 * a small chip beside the kind in the job list, rather than as a section the redesign removed.
 *
 * ⚠️ **Both writes ask first.** *Futtatás most* is the mockup's own confirmation (live data, maybe a
 * customer email). *Vissza a sorba* asks too, which the mockup does not draw: a requeued
 * `mail.send` delivers a real email, and `state-rules.md` §8 wants a confirmation on anything whose
 * effect leaves the screen.
 *
 * ⚠️ **No payload, ever** — S-15. The note above the job list says so in words, because an operator
 * looking for an email's recipient will otherwise assume the column is missing.
 */

type Overview =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly code: string }
  | { readonly state: 'ready'; readonly data: OperatorOperationsResponse };

type Jobs =
  | { readonly state: 'loading' }
  | { readonly state: 'failed' }
  | {
      readonly state: 'ready';
      readonly jobs: readonly OperatorJob[];
      readonly total: number;
      readonly counts: Readonly<Record<string, number>>;
    };

type Pending =
  | { readonly kind: 'run'; readonly chore: OperatorChoreState }
  | { readonly kind: 'requeue'; readonly job: OperatorJob }
  | null;

export function OperationsScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const toast = useToast();

  const [overview, setOverview] = useState<Overview>({ state: 'loading' });
  const [jobs, setJobs] = useState<Jobs>({ state: 'loading' });
  const [filter, setFilter] = useState<OperatorJobState | 'all'>('all');
  const [page, setPage] = useState(0);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const readOverview = useCallback(async (): Promise<void> => {
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.operations);
    if (!result.ok) {
      if (result.status === 401) return;
      setOverview({ state: 'failed', code: result.code });
      return;
    }
    const parsed = operatorOperationsResponseSchema.safeParse(result.value);
    setOverview(
      parsed.success ? { state: 'ready', data: parsed.data } : { state: 'failed', code: 'unknown' },
    );
  }, [session]);

  const readJobs = useCallback(async (): Promise<void> => {
    const result = await session.request<unknown>(
      CONTROL_PANEL_PATHS.jobs(filter === 'all' ? null : filter, page * DEFAULT_PAGE_SIZE),
    );
    if (!result.ok) {
      if (result.status !== 401) setJobs({ state: 'failed' });
      return;
    }
    const parsed = operatorJobListResponseSchema.safeParse(result.value);
    setJobs(
      parsed.success
        ? {
            state: 'ready',
            jobs: parsed.data.data,
            total: parsed.data.meta.total,
            counts: parsed.data.counts,
          }
        : { state: 'failed' },
    );
  }, [session, filter, page]);

  useEffect(() => {
    void readOverview();
  }, [readOverview]);
  useEffect(() => {
    void readJobs();
  }, [readJobs]);
  useEffect(() => {
    setPage(0);
  }, [filter]);

  const confirm = (): void => {
    if (pending === null || busy) return;
    setBusy(true);
    setFailure(null);
    const path =
      pending.kind === 'run'
        ? CONTROL_PANEL_PATHS.runChore(pending.chore.key)
        : CONTROL_PANEL_PATHS.requeueJob(pending.job.id);
    void session.request<unknown>(path, { method: 'POST' }).then((result) => {
      setBusy(false);
      if (!result.ok) {
        if (result.status !== 401) {
          setFailure(
            result.code === 'job_not_failed'
              ? t('ops.error.jobNotFailed')
              : result.code === 'unknown_chore'
                ? t('ops.error.unknownChore')
                : t('error.unknown'),
          );
        }
        setPending(null);
        return;
      }
      toast(
        pending.kind === 'run'
          ? { title: t('ops.chores.started'), description: choreName(pending.chore.key, t) }
          : { title: t('ops.jobs.requeued'), description: pending.job.kind },
      );
      setPending(null);
      void readOverview();
      void readJobs();
    });
  };

  const unhandled = new Set(
    overview.state === 'ready'
      ? overview.data.jobs.filter((kind) => !kind.handled).map((kind) => kind.kind)
      : [],
  );

  return (
    <div data-screen="operations" className="flex flex-col gap-7 p-5">
      {failure === null ? null : (
        <p role="alert" className="text-destructive text-[12.5px]">
          {failure}
        </p>
      )}

      <section aria-label={t('ops.chores.heading')} className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-[14px] font-medium">{t('ops.chores.heading')}</h2>
          {overview.state === 'ready' ? (
            <span className="text-muted-foreground text-[12px]">
              {(() => {
                const late = overview.data.chores.filter((chore) => chore.overdue).length;
                return late > 0 ? t('ops.chores.late', { count: late }) : t('ops.chores.onCadence');
              })()}
            </span>
          ) : null}
        </div>
        {overview.state === 'failed' ? (
          <div role="alert" className="flex flex-col items-start gap-2">
            <p className="text-[13px]">{t('ops.failed')}</p>
            <Button
              variant="outline"
              size="sm"
              className={BTN_SM}
              onClick={() => {
                void readOverview();
              }}
            >
              {t('ops.retry')}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[880px] text-[13px]">
              <thead className="bg-muted/40 border-b">
                <tr className={TH_ROW}>
                  <th className={TH}>{t('ops.chores.column.chore')}</th>
                  <th className={TH}>{t('ops.chores.column.cadence')}</th>
                  <th className={TH}>{t('ops.chores.column.lastRun')}</th>
                  <th className={TH}>{t('ops.chores.column.outcome')}</th>
                  <th className={TH}>{t('ops.chores.column.state')}</th>
                  <th className={cn(TH, 'text-right')}>{t('ops.chores.column.day')}</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {overview.state === 'loading' ? (
                  <SkeletonRows columns={7} rowClassName="h-11" rightAligned={[5]} />
                ) : (
                  overview.data.chores.map((chore) => (
                    <ChoreRow
                      key={chore.key}
                      chore={chore}
                      onRun={() => {
                        setPending({ kind: 'run', chore });
                      }}
                    />
                  ))
                )}
              </tbody>
            </table>
            {overview.state === 'loading' ? (
              <p role="status" className="sr-only">
                {t('ops.loading')}
              </p>
            ) : null}
          </div>
        )}
      </section>

      <section aria-label={t('ops.jobs.heading')} className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-[14px] font-medium">{t('ops.jobs.heading')}</h2>
          {jobs.state === 'ready' ? (
            <span className="text-muted-foreground text-[12px]">
              {(jobs.counts.failed ?? 0) > 0
                ? t('ops.jobs.summary', { count: jobs.counts.failed ?? 0 })
                : t('ops.jobs.summaryNone')}
            </span>
          ) : null}
          <div className="ml-auto w-[160px]">
            <Select
              aria-label={t('ops.jobs.filter')}
              icon="funnel"
              value={filter}
              options={(['all', 'failed', 'pending', 'running', 'done'] as const).map((value) => {
                const counts = jobs.state === 'ready' ? jobs.counts : undefined;
                const n =
                  counts === undefined
                    ? undefined
                    : value === 'all'
                      ? Object.values(counts).reduce((sum, c) => sum + c, 0)
                      : (counts[value] ?? 0);
                return {
                  value,
                  label: t(JOB_FILTER_LABEL[value]),
                  ...(n === undefined ? {} : { hint: formatNumber(n, locale) }),
                };
              })}
              onChange={(event) => {
                setFilter(event.target.value as OperatorJobState | 'all');
              }}
              className={cn(SELECT_SKIN, 'h-7 px-2 text-[12.5px]')}
            />
          </div>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-dashed px-3 py-2">
          <Icon name="shield-check" size={14} className="text-primary mt-px shrink-0" />
          <span className="text-muted-foreground text-[12.5px] text-pretty">
            {t('ops.payloadNote')}
          </span>
        </div>
        {jobs.state === 'failed' ? (
          <p role="alert" className="text-[13px]">
            {t('ops.failed')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[880px] text-[13px]">
              <thead className="bg-muted/40 border-b">
                <tr className={TH_ROW}>
                  <th className={TH}>{t('ops.jobs.column.kind')}</th>
                  <th className={TH}>{t('ops.jobs.column.tenant')}</th>
                  <th className={cn(TH, 'text-right')}>{t('ops.jobs.column.attempts')}</th>
                  <th className={TH}>{t('ops.jobs.column.error')}</th>
                  <th className={TH}>{t('ops.jobs.column.state')}</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {jobs.state === 'loading' ? (
                  <SkeletonRows columns={6} rowClassName="h-11" rightAligned={[2]} />
                ) : (
                  jobs.jobs.map((job) => (
                    <JobRow
                      key={job.id}
                      job={job}
                      unhandled={unhandled.has(job.kind)}
                      onRequeue={() => {
                        setPending({ kind: 'requeue', job });
                      }}
                    />
                  ))
                )}
              </tbody>
            </table>
            {jobs.state === 'loading' ? (
              <p role="status" className="sr-only">
                {t('ops.loading')}
              </p>
            ) : null}
            {jobs.state === 'ready' && jobs.jobs.length === 0 ? (
              <div className="text-muted-foreground px-6 py-10 text-center text-[12.5px]">
                {t('ops.jobsEmpty')}
              </div>
            ) : null}
          </div>
        )}
        {jobs.state === 'ready' && jobs.total > DEFAULT_PAGE_SIZE ? (
          <Pager
            page={page}
            pageCount={Math.ceil(jobs.total / DEFAULT_PAGE_SIZE)}
            total={t('ops.jobs.count', { count: formatNumber(jobs.total, locale) })}
            onChange={setPage}
          />
        ) : null}
      </section>

      <ActionDialog
        open={pending !== null}
        onClose={() => {
          setPending(null);
        }}
        title={
          pending?.kind === 'run'
            ? t('ops.chores.runTitle', { name: choreName(pending.chore.key, t) })
            : t('ops.jobs.requeueTitle')
        }
        description={pending?.kind === 'run' ? t('ops.chores.runBody') : t('ops.jobs.confirm')}
        tier={{
          label: pending?.kind === 'run' ? t('ops.chores.tier') : t('ops.jobs.tier'),
          tone: 'warn',
        }}
        cancelLabel={t('tenant.cancel')}
        confirm={{
          label: pending?.kind === 'run' ? t('ops.chores.runConfirm') : t('ops.jobs.requeue'),
          tone: 'warn',
          disabled: busy,
          onClick: confirm,
        }}
      />
    </div>
  );
}

const JOB_FILTER_LABEL: Readonly<Record<OperatorJobState | 'all', MessageKey>> = {
  all: 'ops.jobs.filter.all',
  failed: 'ops.jobs.state.failed',
  pending: 'ops.jobs.state.pending',
  running: 'ops.jobs.state.running',
  done: 'ops.jobs.state.done',
};

function ChoreRow({
  chore,
  onRun,
}: {
  readonly chore: OperatorChoreState;
  readonly onRun: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const late = chore.overdue;

  /* ⚠️ Cadences missed since the last start, minus the one merely due now — the same arithmetic
     the health strip's tile prints, so the two never disagree. */
  const missed =
    chore.lastStartedAt === null
      ? null
      : Math.max(
          1,
          Math.floor((Date.now() - new Date(chore.lastStartedAt).getTime()) / chore.everyMs) - 1,
        );

  const outcome =
    chore.lastStatus === null
      ? { text: t('ops.never'), tone: 'text-muted-foreground' }
      : chore.lastStatus === 'failed'
        ? { text: chore.lastError ?? t('ops.chore.failed'), tone: 'text-destructive' }
        : chore.lastStatus === 'running'
          ? { text: t('ops.chore.running'), tone: 'text-muted-foreground' }
          : { text: t('ops.chore.succeeded'), tone: 'text-muted-foreground' };

  /* ⚠️ What the run reported — *mailed: 2, tasks: 7* — which the worker used to discard. The
     drawing's cell says only *Sikeres*, so the figures ride on the hover rather than in the row. */
  const detail =
    typeof chore.lastDetail !== 'object' || chore.lastDetail === null
      ? null
      : Object.entries(chore.lastDetail)
          .map(([key, value]) => `${key}: ${String(value)}`)
          .join(', ');

  return (
    <tr className={cn('h-11 border-b last:border-0', late ? 'bg-destructive/[0.05]' : '')}>
      <td className="px-3 font-medium">{choreName(chore.key, t)}</td>
      <td className="text-muted-foreground px-3 whitespace-nowrap">{cadence(chore.everyMs, t)}</td>
      <td className="px-3 whitespace-nowrap tabular-nums">
        {chore.lastStartedAt === null
          ? '–'
          : formatDateTime(chore.lastStartedAt, locale, {
              today: t('when.today'),
              yesterday: t('when.yesterday'),
            })}
      </td>
      <td className="max-w-[260px] truncate px-3" title={detail ?? outcome.text}>
        <span className={cn('text-[12.5px]', outcome.tone)}>{outcome.text}</span>
      </td>
      <td className="px-3">
        <span className={cn(PILL, 'gap-1', late ? TONE.bad : TONE.ok)}>
          {late
            ? missed === null
              ? t('ops.chores.lateShort')
              : t('ops.chores.lateMissed', { count: missed })
            : t('ops.chore.succeeded')}
        </span>
      </td>
      <td
        className={cn(
          'px-3 text-right tabular-nums',
          chore.failures24h > 0 ? 'text-destructive font-medium' : 'text-muted-foreground',
        )}
      >
        {formatNumber(chore.failures24h, locale)}
      </td>
      <td className="px-3 text-right">
        <Button variant="outline" size="sm" className={BTN_SM} onClick={onRun}>
          <Icon name="play" size={12} />
          {t('ops.chores.run')}
        </Button>
      </td>
    </tr>
  );
}

const JOB_TONE: Readonly<Record<'failed' | 'pending' | 'running' | 'done', string>> = {
  failed: TONE.badSoft,
  pending: TONE.muted,
  running: TONE.primary,
  done: TONE.ok,
};

function JobRow({
  job,
  unhandled,
  onRequeue,
}: {
  readonly job: OperatorJob;
  readonly unhandled: boolean;
  readonly onRequeue: () => void;
}): ReactNode {
  const t = useT();
  const state = job.status === 'failed' || job.stalled ? 'failed' : job.status;
  const created = new Date(job.createdAt);
  const time = `${String(created.getHours()).padStart(2, '0')}:${String(created.getMinutes()).padStart(2, '0')}`;

  return (
    <tr className="h-11 border-b last:border-0">
      <td className="px-3">
        <span className="flex items-center gap-1.5">
          <span className="block font-mono text-[12px]">{job.kind}</span>
          {unhandled ? (
            <span className="bg-muted text-muted-foreground rounded px-1 text-[10.5px]">
              {t('ops.queue.unhandled')}
            </span>
          ) : null}
        </span>
        <span className="text-muted-foreground block font-mono text-[11px]">
          {`${job.id.slice(0, 8)} · ${time}`}
        </span>
      </td>
      <td className="max-w-[220px] truncate px-3">{job.tenantName ?? t('ops.jobs.noTenant')}</td>
      <td className="px-3 text-right tabular-nums">{`${String(job.attempts)} / ${String(job.maxAttempts)}`}</td>
      <td
        className={cn(
          'max-w-[300px] truncate px-3 text-[12.5px]',
          job.lastError === null ? 'text-muted-foreground' : 'text-destructive',
        )}
        title={job.lastError ?? ''}
      >
        {job.lastError ?? '–'}
      </td>
      <td className="px-3">
        <span className={cn(PILL, JOB_TONE[state])}>
          {job.stalled ? t('ops.jobs.stalled') : t(JOB_FILTER_LABEL[state])}
        </span>
      </td>
      <td className="px-3 text-right">
        {state === 'failed' ? (
          <Button variant="outline" size="sm" className={BTN_SM} onClick={onRequeue}>
            <Icon name="refresh-cw" size={12} />
            {t('ops.jobs.requeue')}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/** *15 percenként* · *Óránként* · *Naponta* — the chore's cadence in words. */
function cadence(everyMs: number, t: Translate): string {
  const minutes = Math.round(everyMs / 60_000);
  if (minutes < 60) return t('ops.cadence.minutes', { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours === 1) return t('ops.cadence.hourly');
  if (hours < 24) return t('ops.cadence.hours', { n: hours });
  const days = Math.round(hours / 24);
  return days === 1 ? t('ops.cadence.daily') : t('ops.cadence.days', { n: days });
}

/**
 * A chore's name as a person reads it.
 *
 * ⚠️ One literal `t()` per branch, for `i18n:check`. A key this build does not know falls back to
 * the key itself — a newer worker with a new chore shows `new-chore` rather than a blank row.
 */
export function choreName(key: string, t: Translate): string {
  switch (key) {
    case 'alert-scan':
      return t('chore.alertScan');
    case 'due-sweep':
      return t('chore.dueSweep');
    case 'email-avatars':
      return t('chore.emailAvatars');
    case 'request-mail':
      return t('chore.requestMail');
    case 'contract-watch':
      return t('chore.contractWatch');
    case 'alert-digest':
      return t('chore.alertDigest');
    case 'invite-reminder':
      return t('chore.inviteReminder');
    case 'logging-mail':
      return t('chore.loggingMail');
    case 'finance-mail':
      return t('chore.financeMail');
    case 'integration-mail':
      return t('chore.integrationMail');
    case 'due-sweep-prune':
      return t('chore.dueSweepPrune');
    case 'leave-carry-over':
      return t('chore.leaveCarryOver');
    case 'trial-expiry':
      return t('chore.trialExpiry');
    case 'integration-overwatch':
      return t('chore.integrationOverwatch');
    case 'integration-outbound':
      return t('chore.integrationOutbound');
    case 'integration-poll':
      return t('chore.integrationPoll');
    case 'blob-sweep':
      return t('chore.blobSweep');
    default:
      return key;
  }
}
